import { useState, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import brandGradients from '@assets/backgrounds/quicktour/brand-gradients.webp'

interface HeroGradientProps {
  className?: string
  children?: ReactNode
}

/** A random crop of the brand gradient sheet, mirrored at random. */
function pickCrop() {
  return {
    x: Math.round(Math.random() * 100),
    y: Math.round(Math.random() * 100),
    flipX: Math.random() < 0.5 ? -1 : 1,
    flipY: Math.random() < 0.5 ? -1 : 1,
  }
}

/**
 * Backdrop shared by every tour card hero: a different corner of the brand
 * gradient sheet each time a step mounts. The gradient and the mockups it
 * frames are deliberately theme-invariant (fixed colors, not theme tokens):
 * the designs show the same illustration in light and dark, and the mockups
 * imitate light-mode panel chrome.
 */
export function HeroGradient({ className, children }: HeroGradientProps) {
  const [crop] = useState(pickCrop)
  return (
    <div
      aria-hidden="true"
      className={twMerge(
        'relative isolate flex min-h-[139px] w-full flex-col items-start overflow-hidden p-4',
        className,
      )}
    >
      <div
        className="absolute inset-0 -z-10 bg-no-repeat"
        style={{
          backgroundImage: `url(${brandGradients})`,
          // The sheet is 3:1, so 300% wide shows about two swatches.
          backgroundSize: '300% auto',
          backgroundPosition: `${crop.x}% ${crop.y}%`,
          transform: `scale(${crop.flipX}, ${crop.flipY})`,
        }}
      />
      {children}
    </div>
  )
}

/** Card chrome shared by the mockup rows inside the heroes. */
export const heroCardClass = 'rounded-xl corner-smooth border border-on-light bg-white'

export const heroLabelClass = 'type-ui-sm font-medium text-on-light'
